"use client";

import { IconTile } from "@cmp-src/data-display/IconTile";
import { Button } from "@cmp-src/primitives/button";
import {
  WorkplaceRequestElement,
  WorkplaceRequestRelated,
  WorkplaceRequestTypeElement,
  WorkplaceRequestTypeStatus
} from "@gettactic/api";
import type { IconValue } from "@gettactic/components/src/icons";
import { CircleCheck, CircleSlash } from "lucide-react";
import React, { useMemo, useState } from "react";
import { DEFAULT_CATEGORY_COLOR } from "@/features/settings/components/WorkplaceRequestsPage/categories/components/ColorSwatchSelect";
import { SubStatusSelect } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsModal/SubStatusSelect";
import { classes } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsModal/utils";
import { parseIconValue } from "@/features/settings/components/WorkplaceRequestsPage/requests/icon-value";
import {
  getRequestStatusColor,
  RequestStatusPill
} from "@/features/workplace-requests/components/RequestStatusPill";
import { useWorkplaceRequestCategories } from "@/features/workplace-requests/hooks/useWorkplaceRequestCategories";
import {
  type FollowUp,
  resolveSkippedFollowUps
} from "@/features/workplace-requests/lib/followUpRequests";

interface LinkedRequestsSectionProps {
  request: WorkplaceRequestElement;
  /** The request's own type, carrying the workflows that spawn follow-ups. */
  type: WorkplaceRequestTypeElement | undefined;
  /** Every request type, for the children's icons, names and sub-statuses. */
  workplaceRequestTypes: WorkplaceRequestTypeElement[];
  onOpenRequest: (request: WorkplaceRequestRelated) => void;
  /** Persists a child's new status; `status` is a sub-status id or base status. */
  onStatusChange: (requestId: string, status: string) => void | Promise<void>;
}

/**
 * The requests this one spawned, and the follow-ups its approver deliberately
 * left out. Both answer the same question — what did approving this create —
 * so they live in one section rather than two.
 *
 * A child can be moved on without opening it, which is the point of the list:
 * a parent with several follow-ups gets closed out from here. Only someone who
 * may edit that child gets the control; for everyone else the status is a pill.
 */
export const LinkedRequestsSection: React.FC<LinkedRequestsSectionProps> = ({
  request,
  type,
  workplaceRequestTypes,
  onOpenRequest,
  onStatusChange
}) => {
  const children = request.children ?? [];
  const skipped = useMemo(
    () => resolveSkippedFollowUps(request.workflow_runs, type?.workflows),
    [request.workflow_runs, type?.workflows]
  );

  // Same explicit `limit` as everywhere else this list doubles as a lookup: a
  // category outside the backend's default page size would silently lose its
  // color. Cached alongside the detail modal's own fetch, so it costs nothing.
  const categories = useWorkplaceRequestCategories({ limit: 100 });
  const typesById = useMemo(
    () => new Map(workplaceRequestTypes.map((t) => [t.id, t])),
    [workplaceRequestTypes]
  );
  const categoryColorById = useMemo(
    () => new Map(categories.allElements.map((c) => [c.id, c.color])),
    [categories.allElements]
  );

  const typeVisual = (typeId: string | undefined) => {
    const childType = typeId ? typesById.get(typeId) : undefined;
    return {
      name: childType?.name ?? null,
      icon: parseIconValue(childType?.icon) ?? null,
      color:
        categoryColorById.get(childType?.category_id ?? "") ||
        DEFAULT_CATEGORY_COLOR,
      statuses: childType?.statuses ?? []
    };
  };

  if (children.length === 0 && skipped.length === 0) return null;

  return (
    <section className="mt-8">
      <h3 className={classes.sectionHeading}>
        Linked tickets ({children.length})
      </h3>

      <ul className="mt-2 flex flex-col gap-2">
        {children.map((child) => {
          const visual = typeVisual(child.type?.id);
          return (
            <li key={child.id}>
              <LinkedRequestRow
                request={child}
                typeName={visual.name}
                typeColor={visual.color}
                typeIcon={visual.icon}
                typeStatuses={visual.statuses}
                onOpen={() => onOpenRequest(child)}
                onStatusChange={onStatusChange}
              />
            </li>
          );
        })}

        {skipped.map((followUp) => (
          <li key={followUp.workflowId}>
            <SkippedFollowUpRow
              followUp={followUp}
              typeName={(typeId) => typeVisual(typeId).name}
            />
          </li>
        ))}
      </ul>
    </section>
  );
};

interface LinkedRequestRowProps {
  request: WorkplaceRequestRelated;
  typeName: string | null;
  typeColor: string;
  typeIcon: IconValue | null;
  typeStatuses: WorkplaceRequestTypeStatus[];
  onOpen: () => void;
  onStatusChange: (requestId: string, status: string) => void | Promise<void>;
}

const LinkedRequestRow: React.FC<LinkedRequestRowProps> = ({
  request,
  typeName,
  typeColor,
  typeIcon,
  typeStatuses,
  onOpen,
  onStatusChange
}) => {
  const meta = [
    typeName,
    request.display_id ? `REQ-${request.display_id}` : null
  ]
    .filter(Boolean)
    .join(" · ");
  // Resolved per request by the backend (category membership, not just an
  // org-wide role), so a viewer may manage one follow-up and not the next.
  const canEdit = Boolean(request.capabilities?.can_edit);

  const [isCompleting, setIsCompleting] = useState(false);
  // Closing a follow-up is the move this list exists for — a parent is done
  // once its children are — so it gets its own control rather than living
  // three clicks deep in the status menu. A type that names its closed states
  // lands on the first of them; one that names none closes on the base status.
  const closedSubStatus = typeStatuses
    .filter((status) => status.base_status === "closed")
    .sort((a, b) => a.order - b.order)[0];
  const isClosed = request.status === "closed";

  const complete = async () => {
    setIsCompleting(true);
    try {
      await onStatusChange(request.id, closedSubStatus?.id ?? "closed");
    } catch {
      // Reported by the caller; the row falls back to its saved status.
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="flex items-center gap-3 rounded-brand border border-neutral-100 bg-neutral-bg p-3 transition-colors hover:bg-neutral-100">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <IconTile color={typeColor} icon={typeIcon} size="md" bordered />
        <span className="flex min-w-0 flex-col">
          {meta ? (
            <span className="truncate font-inter text-neutral-500 text-xs">
              {meta}
            </span>
          ) : null}
          <span className="truncate font-inter font-semibold text-neutral-900 text-sm">
            {request.title}
          </span>
          <span className="truncate font-inter text-neutral-500 text-xs">
            {request.owner_user?.name
              ? `Assigned to ${request.owner_user.name}`
              : "Unassigned"}
          </span>
        </span>
      </button>

      {canEdit && !isClosed ? (
        <Button
          variant="outline"
          size="sm"
          iconLeft={<CircleCheck className="h-3.5 w-3.5" />}
          isLoading={isCompleting}
          onClick={complete}
          aria-label={`Mark ${request.title} as complete`}
          className="shrink-0 bg-white"
        >
          Mark complete
        </Button>
      ) : null}

      {canEdit ? (
        <SubStatusSelect
          request={request}
          typeStatuses={typeStatuses}
          onStatusChange={onStatusChange}
          renderTrigger={(state) => (
            <button
              type="button"
              className="shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
              aria-label={`Change status of ${request.title}`}
            >
              <RequestStatusPill
                color={getRequestStatusColor(state.baseStatus)}
                label={state.label}
              />
            </button>
          )}
        />
      ) : (
        <RequestStatusPill
          className="shrink-0"
          color={getRequestStatusColor(request.status)}
          label={request.sub_status?.name ?? request.status}
        />
      )}
    </div>
  );
};

interface SkippedFollowUpRowProps {
  followUp: FollowUp;
  typeName: (typeId: string) => string | null;
}

/**
 * A follow-up the approver chose not to create. It has no request to link to —
 * that is the whole point — so it stays a muted line saying what was skipped.
 */
const SkippedFollowUpRow: React.FC<SkippedFollowUpRowProps> = ({
  followUp,
  typeName
}) => {
  const label =
    followUp.children
      .map((child) => child.title || typeName(child.typeId))
      .filter(Boolean)
      .join(", ") ||
    followUp.name ||
    "Follow-up request";

  return (
    <div className="flex items-center gap-3 rounded-brand border border-neutral-200 border-dashed p-3">
      <CircleSlash className="h-4 w-4 shrink-0 text-neutral-400" />
      <span className="min-w-0 flex-1 truncate font-inter text-neutral-500 text-sm">
        {label}
      </span>
      <span className="shrink-0 rounded-full bg-neutral-100 px-2 py-1 font-inter font-medium text-neutral-600 text-xs">
        Skipped by approver
      </span>
    </div>
  );
};
